import createClient from "openapi-fetch";
import type { QueryFunctionContext } from "@tanstack/react-query";
import type { paths } from "@/api/schema";
import { Sentry } from "@/lib/sentry";
import i18n from "@/i18n";

// Same-origin by default (ADR-13: Traefik path-router in prod/test, Vite's
// dev proxy locally) — VITE_API_BASE_URL is normally "". Resolved to the
// current page's own origin rather than left as a relative "" base: several
// libraries here (openapi-fetch's internal `new Request(path)`, used below)
// require an absolute URL and have no browser "current document" to resolve
// a relative one against outside an actual page load — most visibly in
// tests (jsdom/Node) but this keeps the same code path working everywhere,
// not just in a real browser tab. `||`, not `??`, on purpose: "" must fall
// through same as unset.
export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:8000");

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Laravel's 422 `message` is only the first error plus "(and N more errors)".
 * Every caller already surfaces `error.message` in a toast, so rewriting it
 * here to the full, de-duplicated list of field messages (already localized
 * server-side via Accept-Language) fixes all of them in one place.
 */
export function validationSummary(body: unknown): string | null {
  if (!body || typeof body !== "object" || !("errors" in body)) return null;
  const errors = (body as { errors: unknown }).errors;
  if (!errors || typeof errors !== "object") return null;

  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((message): message is string => typeof message === "string" && message !== "");

  return messages.length > 0 ? [...new Set(messages)].join(" ") : null;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Sanctum SPA session expired or was never established — reset auth state. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/** Primes the XSRF-TOKEN cookie required before any mutating request (Sanctum SPA flow). */
export async function ensureCsrfCookie(): Promise<void> {
  await fetch(`${API_BASE_URL}/sanctum/csrf-cookie`, { credentials: "include" });
}

export const apiClient = createClient<paths>({
  baseUrl: `${API_BASE_URL}/api`,
  credentials: "include",
});

apiClient.use({
  onRequest({ request }) {
    // Server picks validation-message language from this (SetLocale middleware).
    request.headers.set("Accept-Language", i18n.language || "uz");
    if (MUTATING_METHODS.has(request.method)) {
      const token = readCookie("XSRF-TOKEN");
      if (token) {
        request.headers.set("X-XSRF-TOKEN", token);
      }
    }
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status === 401) {
      unauthorizedListeners.forEach((listener) => listener());
    }
    // 5xx only — 4xx is an expected validation/business response the caller
    // already handles, not a bug to page anyone about (mirrors the backend's
    // own MobileApiException status split, bootstrap/app.php).
    if (response.status >= 500) {
      Sentry.captureException(new Error(`${request.method} ${response.url} -> ${response.status}`), {
        extra: {
          url: response.url,
          status: response.status,
          request_id: response.headers.get("X-Request-Id"),
        },
      });
    }
    if (response.status === 422 && response.headers.get("content-type")?.includes("application/json")) {
      const body = await response.clone().json().catch(() => null);
      const summary = validationSummary(body);
      if (summary) {
        // Body is re-serialized — the original length/encoding no longer apply.
        const headers = new Headers(response.headers);
        headers.delete("content-length");
        headers.delete("content-encoding");
        return new Response(JSON.stringify({ ...body, message: summary }), {
          status: response.status,
          statusText: response.statusText,
          headers,
        });
      }
    }
    return response;
  },
});

/**
 * Default React Query `queryFn`: treats `queryKey[0]` as an API path and
 * `queryKey[1]` (if present) as the openapi-fetch request options (params, fetch, etc.).
 * Queries that need something other than a plain GET keep passing their own `queryFn`.
 */
export async function defaultQueryFn({ queryKey }: QueryFunctionContext) {
  const [path, options] = queryKey as [keyof paths, Record<string, unknown>?];
  const { data, error } = await apiClient.GET(path as never, options as never);

  if (error) {
    throw error;
  }

  return data;
}
